/* eslint-disable no-restricted-imports, ember/no-classic-classes -- ember-cp-validations requires EmberObject */

import { buildValidations, validator } from 'ember-cp-validations';
import { dateFormat } from 'client-portal/utils/date-time';
import ENV from 'client-portal/config/environment';
import EmberObject from '@ember/object';
import moment from 'moment-timezone';

// See `isWaitlistRecaptchaDisabled` in config/environment.js: only local dev hides the
// widget and skips the token. Test/staging/production keep reCAPTCHA on.
export const IS_RECAPTCHA_DISABLED = !!ENV.isWaitlistRecaptchaDisabled;

export const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const ALL_DAY_SLOT = 'All day';

export const TIME_SLOTS = [ALL_DAY_SLOT, 'Morning', 'Afternoon', 'Evening'];

export const INSURANCE_TYPES = {
  SELF_PAY: 'Self-Pay',
  INSURANCE: 'Insurance',
};

function emptyAvailability() {
  return WEEKDAYS.reduce((acc, day) => {
    acc[day] = [];
    return acc;
  }, {});
}

const Validations = buildValidations({
  firstName: [
    validator('presence', {
      presence: true,
      ignoreBlank: true,
      descriptionKey: 'site.waitlistIntake.fields.legalFirstName',
      messageKey: 'errors.presence',
    }),
    validator('length', { min: 1, max: 100, allowBlank: true }),
  ],
  lastName: [
    validator('presence', {
      presence: true,
      ignoreBlank: true,
      descriptionKey: 'site.waitlistIntake.fields.legalLastName',
      messageKey: 'errors.presence',
    }),
    validator('length', { min: 1, max: 100, allowBlank: true }),
  ],
  preferredName: validator('length', {
    max: 128,
    allowBlank: true,
    descriptionKey: 'site.waitlistIntake.fields.preferredName',
  }),
  dateOfBirth: [
    validator('presence', {
      presence: true,
      ignoreBlank: true,
      descriptionKey: 'site.waitlistIntake.fields.dateOfBirth',
      messageKey: 'errors.presence',
    }),
    validator('inline', {
      validate(value, _options, model) {
        if (!value) {
          return true;
        }

        let date = moment(value, dateFormat, true);
        let minDate = moment('1900-01-01', 'YYYY-MM-DD');
        if (!date.isValid() || date.isBefore(minDate, 'day') || !date.isBefore(moment(), 'day')) {
          return model.intl.t('errors.prospectiveClient.invalidBirthDate');
        }

        return true;
      },
    }),
  ],
  insuranceType: validator('presence', {
    presence: true,
    ignoreBlank: true,
    descriptionKey: 'site.waitlistIntake.fields.insuranceType',
    messageKey: 'errors.presence',
  }),
  email: [
    validator('presence', {
      presence: true,
      ignoreBlank: true,
      messageKey: 'errors.emailRequired',
    }),
    validator('format', {
      type: 'email',
      allowBlank: true,
      messageKey: 'errors.invalidEmail',
    }),
    validator('length', {
      max: 191,
      allowBlank: true,
      descriptionKey: 'site.waitlistIntake.fields.email',
    }),
  ],
  phone: [
    validator('presence', {
      presence: true,
      ignoreBlank: true,
      messageKey: 'errors.phone.required',
    }),
    validator('length', {
      is: 10,
      allowBlank: true,
      messageKey: 'errors.phone.invalid_length',
    }),
  ],
  reasonForVisit: [
    validator('presence', {
      presence: true,
      ignoreBlank: true,
      descriptionKey: 'site.waitlistIntake.fields.reasonForVisit',
      messageKey: 'errors.presence',
    }),
    validator('length', { max: 500, allowBlank: true }),
  ],
  availability: validator('inline', {
    validate(value, _options, model) {
      if (!value || typeof value !== 'object') {
        return model.intl.t('site.waitlistIntake.errors.availabilityRequired');
      }

      let hasSlot = WEEKDAYS.some(day => Array.isArray(value[day]) && value[day].length > 0);
      if (!hasSlot) {
        return model.intl.t('site.waitlistIntake.errors.availabilityRequired');
      }

      return true;
    },
  }),
  recaptchaResponse: [
    validator('presence', {
      presence: true,
      ignoreBlank: true,
      disabled: IS_RECAPTCHA_DISABLED,
      descriptionKey: 'errors.prospectiveClient.recaptchaResponse',
      messageKey: 'errors.prospectiveClient.recaptchaRequired',
    }),
  ],
});

export default EmberObject.extend(Validations, {
  intl: null,
  firstName: '',
  lastName: '',
  preferredName: '',
  dateOfBirth: '',
  insuranceType: null,
  email: '',
  emailConsent: true,
  phone: '',
  phoneConsent: true,
  reasonForVisit: '',
  availability: null,
  recaptchaResponse: '',
  isDirty: false,

  init() {
    this._super(...arguments);

    if (!this.availability) {
      this.set('availability', emptyAvailability());
    }
  },

  serialize() {
    let preferredName = this.preferredName?.trim();
    let phoneDigits = (this.phone || '').replace(/\D/g, '');

    return {
      firstName: this.firstName?.trim(),
      lastName: this.lastName?.trim(),
      ...(preferredName ? { preferredName } : {}),
      dateOfBirth: this.dateOfBirth,
      insuranceType: this.insuranceType,
      email: this.email?.trim(),
      emailConsent: Boolean(this.emailConsent),
      phone: phoneDigits,
      phoneConsent: Boolean(this.phoneConsent),
      reasonForVisit: this.reasonForVisit?.trim(),
      availability: WEEKDAYS.reduce((acc, day) => {
        acc[day] = [...(this.availability?.[day] || [])];
        return acc;
      }, {}),
    };
  },

  serializeForApi() {
    return {
      ...this.serialize(),
      // Token is obtained at submit time; never persist it in the autosave draft.
      recaptchaResponse: this.recaptchaResponse || '',
    };
  },

  restore(data = {}) {
    let {
      firstName,
      lastName,
      preferredName,
      dateOfBirth,
      insuranceType,
      email,
      emailConsent,
      phone,
      phoneConsent,
      reasonForVisit,
      availability,
    } = data;

    this.setProperties({
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      preferredName: preferredName ?? '',
      dateOfBirth: dateOfBirth ?? '',
      insuranceType: insuranceType ?? null,
      email: email ?? '',
      emailConsent: emailConsent ?? true,
      // Normalize in case a draft still has punctuation from an older client build.
      phone: (phone || '').replace(/\D/g, ''),
      phoneConsent: phoneConsent ?? true,
      reasonForVisit: reasonForVisit ?? '',
      availability: availability
        ? { ...emptyAvailability(), ...availability }
        : emptyAvailability(),
    });
  },
});
